'use client';
import React, { useRef, useState, useEffect, type ReactNode } from 'react';
import type { PostMeta, PostAuthor } from './types';
import { brandExternalUrl } from '@/lib/brand-urls';
import { toEditorial, NEWSROOM_CATEGORY } from '@/lib/editorial-categories';
import { stripHtml } from '@/lib/strip-html';

const RESOURCE_CAT_SEG: Record<string, string> = {
  'White Paper': 'white-papers',
  'Case Study': 'case-studies',
  'Industry Report': 'industry-reports',
  'Brochure': 'brochures',
};

const ALL_BRAND_OPTIONS: { value: string; label: string }[] = [
  { value: 'innovaccer', label: 'Innovaccer' },
  { value: 'gravity',    label: 'Gravity' },
  { value: 'flow',       label: 'Flow' },
  { value: 'galaxy',     label: 'Galaxy' },
  { value: 'comet',      label: 'Comet' },
  { value: 'atlas',      label: 'Atlas' },
  { value: 'storyhealth', label: 'Story Health' },
];
function publicPostUrl(slug: string, brand: string | undefined, category: string | undefined, contentType: string | undefined, isResource: boolean): string {
  // Newsroom is set via the Content Type field, not Category — without this,
  // brandExternalUrl never sees "Newsroom" and defaults to the blogs path,
  // so both branded and unbranded Newsroom posts got the wrong URL hint here.
  const isNewsroom = contentType === 'Newsroom' || category === 'Newsroom';
  const external = brandExternalUrl(brand, slug, isNewsroom ? 'Newsroom' : category);
  if (external) return external;
  if (isResource) {
    const seg = category && RESOURCE_CAT_SEG[category];
    return seg ? `/resources/${seg}/${slug}` : `/resources/${slug}`;
  }
  if (isNewsroom) return `/news/${slug}`;
  return `/blogs/${slug}`;
}

function slugify(s: string) {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || '';
}

// Intermediate slugify: allows trailing hyphen so user can type "word-word"
function slugifyLive(s: string) {
  return s.toLowerCase().replace(/[^a-z0-9-]+/g, '-').replace(/^-+/, '');
}

interface Props {
  meta: PostMeta;
  onMetaChange: (patch: Partial<PostMeta>) => void;
  onClose: () => void;
  uploadFile: (file: File) => Promise<string>;
  slug?: string;
  onSlugChange?: (slug: string) => void;
  isNewPost?: boolean;
  isResource?: boolean;
  fieldErrors?: Record<string, string>;
  onClearError?: (key: string) => void;
  onSave?: () => void;
  saving?: boolean;
  publishToggle?: ReactNode;
  hasErrors?: boolean;
  /** True when brand was pre-filled from the admin dashboard this post was
   * created from (e.g. "+ New Post" on /flow/admin) — the dropdown is locked
   * so it can't be accidentally changed away from the dashboard it belongs to. */
  brandLocked?: boolean;
  /** Shared with the main-canvas title heading so editing the title from
   * either place has the same "auto-derive slug until manually touched"
   * behavior, and survives Settings being closed/reopened. Falls back to a
   * local ref if not provided (e.g. other callers of this panel). */
  slugEditedRef?: { current: boolean };
  /** Brands beyond meta.brand to also create a copy of this post for —
   * offered whenever not brandLocked, for new and already-saved posts
   * alike. See BlogEditor's save(). */
  extraBrands?: string[];
  onExtraBrandsChange?: (brands: string[]) => void;
}

const RequiredMark = () => (
  <span style={{ color: '#b91c1c', marginLeft: 3, fontWeight: 700 }} title="Required">*</span>
);

function FieldError({ msg }: { msg?: string }) {
  if (!msg) return null;
  return <span style={{ display: 'block', fontSize: 11, color: '#b91c1c', marginTop: 3, fontFamily: 'var(--seasons-font-polysans, system-ui)' }}>✕ {msg}</span>;
}

export function FrontmatterPanel({ meta, onMetaChange, onClose, uploadFile, slug, onSlugChange, isNewPost, isResource, fieldErrors, onClearError, onSave, saving, publishToggle, hasErrors, brandLocked, slugEditedRef, extraBrands, onExtraBrandsChange }: Props) {
  // Newsroom is no longer a manually-selectable Content Type — it's only ever
  // set by creating a post via the "+ New Newsroom" admin tab. Once a post is
  // Newsroom, hide the field entirely rather than exposing a dropdown that no
  // longer lists Newsroom as an option (which would silently reclassify it).
  const isNewsroomPost = toEditorial(meta.category || '', meta.contentType) === NEWSROOM_CATEGORY;
  const heroInputRef = useRef<HTMLInputElement>(null);
  const metaImageInputRef = useRef<HTMLInputElement>(null);
  const pdfInputRef = useRef<HTMLInputElement>(null);
  const audioInputRef = useRef<HTMLInputElement>(null);
  const localSlugEdited = useRef(false);
  const slugEdited = slugEditedRef ?? localSlugEdited;
  const [pdfUploading, setPdfUploading] = useState(false);
  const [audioUploading, setAudioUploading] = useState(false);
  const [heroMirroring, setHeroMirroring] = useState(false);
  // Seeded from existing data (a post already has an External Link → open on
  // that mode), but otherwise a plain UI toggle — switching doesn't clear any
  // other field, so nothing is lost by flipping back and forth.
  const [contentMode, setContentMode] = useState<'custom' | 'external'>(
    (meta.newsLink || '').trim() ? 'external' : 'custom'
  );
  // Seed Meta Title/Description from Title/Dek on first open when they're
  // empty, so the field shows the real value that will actually be used
  // (editable, not just placeholder text) instead of looking blank. Runs
  // once per mount — later edits (including deliberately clearing a field)
  // aren't overwritten.
  useEffect(() => {
    const patch: Partial<PostMeta> = {};
    if (!meta.metaTitle?.trim() && meta.title?.trim()) patch.metaTitle = stripHtml(meta.title);
    if (!meta.metaDescription?.trim() && meta.dek?.trim()) patch.metaDescription = meta.dek;
    if (Object.keys(patch).length > 0) onMetaChange(patch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Local display value for the slug input — allows intermediate trailing hyphens
  const [slugDisplay, setSlugDisplay] = useState(slug || '');
  useEffect(() => {
    if (!slugEdited.current) setSlugDisplay(slug || '');
  }, [slug]);

  const updateAuthor = (idx: number, patch: Partial<PostAuthor>) => {
    const authors = [...(meta.authors || [])];
    authors[idx] = { ...authors[idx], ...patch };
    onMetaChange({ authors });
  };
  const addAuthor = () => onMetaChange({ authors: [...(meta.authors || []), { name: '', avatar: '', profileUrl: '' }] });
  const removeAuthor = (idx: number) => {
    const authors = (meta.authors || []).filter((_, i) => i !== idx);
    onMetaChange({ authors });
  };

  const uploadAvatar = async (idx: number, file: File) => {
    const url = await uploadFile(file);
    updateAuthor(idx, { avatar: url });
  };

  const uploadHero = async (file: File) => {
    const url = await uploadFile(file);
    onMetaChange({ heroImg: url });
  };

  const uploadPdf = async (file: File) => {
    setPdfUploading(true);
    try {
      const url = await uploadFile(file);
      onMetaChange({ pdfUrl: url });
    } finally {
      setPdfUploading(false);
    }
  };

  const uploadAudio = async (file: File) => {
    setAudioUploading(true);
    try {
      const url = await uploadFile(file);
      onMetaChange({ audioUrl: url });
    } finally {
      setAudioUploading(false);
    }
  };

  const mirrorHeroToGcs = async (rawUrl: string) => {
    if (!rawUrl || rawUrl.includes('storage.googleapis.com/cms_rewire')) return;
    setHeroMirroring(true);
    try {
      const res = await fetch('/api/media/download', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: rawUrl }),
      });
      if (res.ok) {
        const { url: gcsUrl } = await res.json();
        onMetaChange({ heroImg: gcsUrl });
      }
      // If host not allowlisted or fetch failed, keep the raw URL as-is
    } catch { /* keep raw URL */ } finally {
      setHeroMirroring(false);
    }
  };

  return (
    <>
      <div className="be-settings-overlay" onClick={onClose} />
      <aside className="be-settings-panel">
        <div className="be-settings-panel__head">
          <span className="be-settings-panel__title">Post Settings</span>
          <button className="be-settings-panel__close" onClick={onClose} aria-label="Close settings">
            <svg width="16" height="16" fill="currentColor" viewBox="0 0 256 256" aria-hidden="true">
              <path d="M205.66,194.34a8,8,0,0,1-11.32,11.32L128,139.31,61.66,205.66a8,8,0,0,1-11.32-11.32L116.69,128,50.34,61.66A8,8,0,0,1,61.66,50.34L128,116.69l66.34-66.35a8,8,0,0,1,11.32,11.32L139.31,128Z"/>
            </svg>
          </button>
        </div>

        <div className="be-settings-panel__body">
          {/* Content mode — Custom Content shows the full editor as usual;
              External Link strips everything down to what a link-out card
              actually needs and hides the rest. `contentMode` is local UI
              state re-derived on every load from whether `newsLink` is set
              (see useState above) — it isn't its own persisted field. So
              switching to Custom Content must actually clear `newsLink`;
              otherwise the saved post still carries a link, and reopening
              the editor re-derives External Link from it, making the switch
              look like it didn't take. Switching to External Link doesn't
              restore a previous link — the user re-enters it. */}
          <div className="be-field">
            <div style={{ display: 'flex', border: '1px solid var(--be-border, #d4d4d4)', borderRadius: 8, overflow: 'hidden' }}>
              {(['custom', 'external'] as const).map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => {
                    setContentMode(m);
                    if (m === 'custom' && (meta.newsLink || '').trim()) {
                      onMetaChange({ newsLink: undefined });
                    }
                  }}
                  style={{
                    flex: 1,
                    padding: '8px 12px',
                    fontSize: 13,
                    fontWeight: 500,
                    fontFamily: 'inherit',
                    border: 'none',
                    cursor: 'pointer',
                    background: contentMode === m ? 'var(--be-accent, #053bee)' : 'var(--be-surface-1, #fff)',
                    color: contentMode === m ? '#fff' : 'var(--be-text-1, #111)',
                  }}
                >
                  {m === 'custom' ? 'Custom Content' : 'External Link'}
                </button>
              ))}
            </div>
            <span className="be-field__hint">
              Choose <strong>Custom Content</strong> to write your own post, or <strong>External Link</strong> to link out.
            </span>
          </div>

          {/* Title */}
          <div className="be-field">
            <label className="be-field__label">Title<RequiredMark /></label>
            <input
              className="be-field__input"
              value={meta.title || ''}
              onChange={(e) => {
                onMetaChange({ title: e.target.value });
                if (isNewPost && !slugEdited.current) {
                  onSlugChange?.(slugify(e.target.value));
                }
                if (e.target.value.trim()) onClearError?.('title');
              }}
              placeholder="Post title"
              style={fieldErrors?.title ? { border: '1px solid #fca5a5' } : undefined}
            />
            <FieldError msg={fieldErrors?.title} />
          </div>

          {/* Slug — always editable */}
          {contentMode === 'custom' && (
            <div className="be-field">
              <label className="be-field__label">Slug</label>
              <input
                className="be-field__input"
                value={slugDisplay}
                onChange={(e) => {
                  slugEdited.current = true;
                  const live = slugifyLive(e.target.value);
                  setSlugDisplay(live);
                  onSlugChange?.(slugify(live));
                  onClearError?.('slug');
                }}
                onBlur={(e) => {
                  const clean = slugify(e.target.value);
                  setSlugDisplay(clean);
                  onSlugChange?.(clean);
                }}
                placeholder="post-url-slug"
                spellCheck={false}
                style={fieldErrors?.slug ? { border: '1px solid #fca5a5' } : undefined}
              />
              <FieldError msg={fieldErrors?.slug} />
              {slug && (
                <span className="be-field__hint">
                  {publicPostUrl(slug, meta.brand, meta.category, meta.contentType, !!isResource)}
                </span>
              )}
            </div>
          )}

          {/* Post Type — controls which admin tab + which content directory */}
          <div className="be-field">
            <label className="be-field__label">
              Post Type<RequiredMark />
              <span style={{ fontWeight: 400, color: 'var(--be-text-secondary, #666)', fontSize: 12, marginLeft: 6 }}>
                (determines admin tab)
              </span>
            </label>
            <select
              className="be-field__select"
              value={isResource ? (meta.category || '') : isNewsroomPost ? 'newsroom' : 'blog'}
              disabled
              onChange={(e) => {
                const val = e.target.value;
                if (val === 'blog') {
                  onMetaChange({ category: '' });
                } else {
                  onMetaChange({ category: val });
                }
                onClearError?.('category');
              }}
              style={{ ...(fieldErrors?.category ? { border: '1px solid #fca5a5' } : {}), opacity: 0.6, cursor: 'not-allowed' }}
            >
              <option value="blog">Blog Post (Stories)</option>
              <option value="newsroom">Newsroom</option>
              <option value="White Paper">White Paper</option>
              <option value="Case Study">Case Study</option>
              <option value="Industry Report">Industry Report</option>
              <option value="Brochure">Brochure</option>
            </select>
            <FieldError msg={fieldErrors?.category} />
          </div>

          {/* Topic Category — blog posts only */}
          {!isResource && (
            <div className="be-field">
              <label className="be-field__label">
                {contentMode === 'custom' ? <>Category (Topic)<RequiredMark /></> : 'Category (Topic)'}
              </label>
              <select
                className="be-field__select"
                value={meta.category || ''}
                onChange={(e) => { onMetaChange({ category: e.target.value }); if (e.target.value) onClearError?.('category'); }}
                style={fieldErrors?.category ? { border: '1px solid #fca5a5' } : undefined}
              >
                <option value="">— Select a topic —</option>
                <option value="AI">AI</option>
                <option value="Clinical Documentation">Clinical Documentation</option>
                <option value="Industry & Company">Industry &amp; Company</option>
                <option value="Data & Interoperability">Data &amp; Interoperability</option>
                <option value="Value-Based Care">Value-Based Care</option>
                <option value="Population Health">Population Health</option>
                <option value="Care Management">Care Management</option>
                <option value="RCM">RCM</option>
                <option value="Risk Adjustment">Risk Adjustment</option>
                <option value="Prior Authorization">Prior Authorization</option>
                <option value="Policy & Compliance">Policy &amp; Compliance</option>
                <option value="Patient Engagement">Patient Engagement</option>
                <option value="Patient Access">Patient Access</option>
                <option value="Mental Health">Mental Health</option>
                <option value="Telehealth">Telehealth</option>
                <option value="Denial Management">Denial Management</option>
                <option value="Autonomous Coding">Autonomous Coding</option>
              </select>
              <FieldError msg={fieldErrors?.category} />
            </div>
          )}

          {/* Newsroom Type — which /news category tab(s) this post belongs under.
              Multi-select: a post can show up under more than one tab (e.g. both
              "Announcements" and "In the news"), but the "All" tab always shows
              exactly one card per post regardless of how many are checked. */}
          {isNewsroomPost && (
            <div className="be-field">
              <label className="be-field__label">Newsroom Type<RequiredMark /></label>
              <div className="be-checkbox-group" style={fieldErrors?.newsroomType ? { border: '1px solid #fca5a5' } : undefined}>
                {(['Announcements', 'In the news', 'Awards'] as const).map((opt) => {
                  const selected = meta.newsroomType || [];
                  const checked = selected.includes(opt);
                  return (
                    <label key={opt} className="be-checkbox-option">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => {
                          const next = e.target.checked
                            ? [...selected, opt]
                            : selected.filter((v) => v !== opt);
                          onMetaChange({ newsroomType: next.length ? next : undefined });
                          if (next.length) onClearError?.('newsroomType');
                        }}
                      />
                      {opt}
                    </label>
                  );
                })}
              </div>
              <FieldError msg={fieldErrors?.newsroomType} />
            </div>
          )}

          {/* Content Type — stories only, hidden for Newsroom (see isNewsroomPost above)
              and disabled for Story Health for now (no editorial tab structure yet). */}
          {!isResource && !isNewsroomPost && meta.brand !== 'storyhealth' && (
            <div className="be-field">
              <label className="be-field__label">
                Content Type
                <span style={{ fontWeight: 400, color: 'var(--be-text-secondary, #666)', fontSize: 12, marginLeft: 6 }}>
                  (filter tab on /blogs)
                </span>
              </label>
              <select
                className="be-field__select"
                value={meta.contentType || ''}
                onChange={(e) => onMetaChange({ contentType: e.target.value })}
              >
                <option value="">— Auto (based on category) —</option>
                <option value="Insights">Insights</option>
                <option value="Customers">Customers</option>
                <option value="Product">Product</option>
                <option value="Conversations">Conversations</option>
                <option value="Case Study">Case Study</option>
              </select>
            </div>
          )}


          {/* Dek — stories only (resources edit description inline in the editor body) */}
          {!isResource && (
            <div className="be-field">
              <label className="be-field__label">Subtitle / Dek</label>
              <textarea
                className="be-field__textarea"
                value={meta.dek || ''}
                onChange={(e) => onMetaChange({ dek: e.target.value })}
                placeholder="One or two sentence summary shown below the title"
                rows={3}
              />
            </div>
          )}

          {/* Brand — main /admin only offers Innovaccer/Gravity; a brand-specific
              admin (Flow/Comet/Galaxy) locks the dropdown to that one brand.
              The current value is always included even if outside that set,
              so an existing post's brand is never hidden/orphaned.
              Unlocked posts (new or already-saved) get a multi-select
              instead — checking more than one brand creates an independent
              copy for each newly-checked brand on save (see BlogEditor's
              save()), the same "replicate across brands" concept as
              ReplicateButton. Stays a multi-select after the first save too,
              so brands can keep being added (or unchecked, before they're
              ever saved) rather than reverting to a single picker. */}
          {(() => {
            const currentBrand = meta.brand || 'innovaccer';

            if (!brandLocked) {
              const selected = new Set([currentBrand, ...(extraBrands || [])]);
              const toggle = (value: string) => {
                if (value === currentBrand) {
                  // Deselecting the primary: promote the first extra brand
                  // (if any) to primary — at least one must stay selected.
                  const rest = extraBrands || [];
                  if (rest.length > 0) {
                    onMetaChange({ brand: rest[0] });
                    onExtraBrandsChange?.(rest.slice(1));
                  }
                  return;
                }
                const rest = extraBrands || [];
                onExtraBrandsChange?.(
                  rest.includes(value) ? rest.filter(b => b !== value) : [...rest, value]
                );
              };
              return (
                <div className="be-field">
                  <label className="be-field__label">
                    Brand(s)
                    <span style={{ fontWeight: 400, color: 'var(--be-text-secondary, #666)', fontSize: 12, marginLeft: 6 }}>
                      (pick more than one to create a copy for each)
                    </span>
                  </label>
                  <div className="be-checkbox-group">
                    {ALL_BRAND_OPTIONS.map(o => (
                      <label key={o.value} className="be-checkbox-option">
                        <input type="checkbox" checked={selected.has(o.value)} onChange={() => toggle(o.value)} />
                        {o.label}
                      </label>
                    ))}
                  </div>
                  {selected.size > 1 && (
                    <span className="be-field__hint">
                      Saving will create a copy for each newly-checked brand ({selected.size} selected in total).
                    </span>
                  )}
                </div>
              );
            }

            // Reached only when brandLocked — the multi-select above covers
            // every unlocked case, new or existing post alike.
            return (
              <div className="be-field">
                <label className="be-field__label">Brand</label>
                <select
                  className="be-field__select"
                  value={currentBrand}
                  onChange={(e) => onMetaChange({ brand: e.target.value || undefined })}
                  disabled
                  style={{ opacity: 0.6, cursor: 'not-allowed' }}
                >
                  <option value="">— None —</option>
                  {ALL_BRAND_OPTIONS.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
                <span className="be-field__hint">Set automatically from the dashboard this post was created from — can't be changed here.</span>
              </div>
            );
          })()}

          {/* Tags */}
          {contentMode === 'custom' && (
          <div className="be-field">
            <label className="be-field__label">
              Topic Tags
              <span style={{ fontWeight: 400, color: 'var(--be-text-secondary, #666)', fontSize: 12, marginLeft: 6 }}>
                (shown at bottom of article)
              </span>
            </label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {(meta.tags || []).map((t, idx) => (
                <div key={idx} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <input
                    className="be-field__input"
                    value={t.tag || ''}
                    onChange={(e) => {
                      const tags = [...(meta.tags || [])];
                      tags[idx] = { tag: e.target.value };
                      onMetaChange({ tags });
                    }}
                    placeholder="e.g. Clinical AI"
                    style={{ flex: 1, fontSize: 13, padding: '5px 8px' }}
                  />
                  <button
                    type="button"
                    className="be-author-remove"
                    onClick={() => {
                      const tags = (meta.tags || []).filter((_, i) => i !== idx);
                      onMetaChange({ tags });
                    }}
                    aria-label="Remove tag"
                  >
                    <svg width="13" height="13" fill="currentColor" viewBox="0 0 256 256" aria-hidden="true">
                      <path d="M205.66,194.34a8,8,0,0,1-11.32,11.32L128,139.31,61.66,205.66a8,8,0,0,1-11.32-11.32L116.69,128,50.34,61.66A8,8,0,0,1,61.66,50.34L128,116.69l66.34-66.35a8,8,0,0,1,11.32,11.32L139.31,128Z"/>
                    </svg>
                  </button>
                </div>
              ))}
              <button
                type="button"
                className="be-add-btn"
                onClick={() => onMetaChange({ tags: [...(meta.tags || []), { tag: '' }] })}
              >
                <svg width="12" height="12" fill="currentColor" viewBox="0 0 256 256" aria-hidden="true">
                  <path d="M224,128a8,8,0,0,1-8,8H136v80a8,8,0,0,1-16,0V136H40a8,8,0,0,1,0-16h80V40a8,8,0,0,1,16,0v80h80A8,8,0,0,1,224,128Z"/>
                </svg>
                Add tag
              </button>
            </div>
          </div>
          )}

          {/* Date */}
          <div className="be-field">
            <label className="be-field__label">Publish Date<RequiredMark /></label>
            <input
              type="date"
              className="be-field__input"
              value={meta.date ? meta.date.slice(0, 10) : ''}
              onChange={(e) => {
                // Anchor at noon local-time-as-UTC so the displayed date matches
                // the stored one across timezones (avoids 'YYYY-MM-DD' parsed as
                // UTC midnight rendering as the previous day west of UTC).
                onMetaChange({ date: e.target.value ? new Date(e.target.value + 'T12:00:00').toISOString() : '' });
                if (e.target.value) onClearError?.('date');
              }}
              style={fieldErrors?.date ? { border: '1px solid #fca5a5' } : undefined}
            />
            <FieldError msg={fieldErrors?.date} />
          </div>

          {/* Created / Updated dates — Settings tab only, NOT rendered on the public page */}
          {contentMode === 'custom' && (
          <div className="be-field">
            <label className="be-field__label">Created Date</label>
            <input
              type="date"
              className="be-field__input"
              value={meta.createdDate ? meta.createdDate.slice(0, 10) : ''}
              onChange={(e) => onMetaChange({ createdDate: e.target.value || '' })}
            />
            <span className="be-field__hint">Internal audit field — not shown on the public page</span>
          </div>
          )}

          {contentMode === 'custom' && (
          <div className="be-field">
            <label className="be-field__label">Updated Date</label>
            <input
              type="date"
              className="be-field__input"
              value={meta.updatedDate ? meta.updatedDate.slice(0, 10) : ''}
              onChange={(e) => onMetaChange({ updatedDate: e.target.value || '' })}
            />
            <span className="be-field__hint">Internal audit field — not shown on the public page</span>
          </div>
          )}

          <div className="be-section-divider" />

          {/* Hero image */}
          <div className="be-field">
            <label className="be-field__label">
              Hero Cover Image<RequiredMark />
            </label>
            <p style={{ fontSize: 11, color: 'var(--be-text-secondary, #666)', margin: '0 0 6px', lineHeight: 1.4 }}>
              {isResource
                ? 'Required — shown as the card thumbnail on the Resources index page.'
                : 'Required — shown at the top of the article and as the card thumbnail on the blog listing.'}
            </p>
            <div
              className="be-field__img-preview"
              onClick={() => heroInputRef.current?.click()}
              title="Click to upload"
              style={fieldErrors?.heroImg ? { border: '1px solid #fca5a5' } : undefined}
            >
              {meta.heroImg ? (
                <img src={meta.heroImg} alt="Hero" />
              ) : (
                <div className="be-field__img-preview__placeholder">
                  <svg width="24" height="24" fill="currentColor" viewBox="0 0 256 256" aria-hidden="true" style={{ opacity: 0.4 }}><path d="M216,40H40A16,16,0,0,0,24,56V200a16,16,0,0,0,16,16H216a16,16,0,0,0,16-16V56A16,16,0,0,0,216,40Zm0,16V158.75l-26.07-26.06a16,16,0,0,0-22.63,0l-20,20-44-44a16,16,0,0,0-22.62,0L40,149.37V56ZM40,200V172l52-52,44,44a8,8,0,0,0,11.31,0l24-24,36.07,36.06L216,200Zm140-128a12,12,0,1,1-12-12A12,12,0,0,1,180,72Z"/></svg>
                  <span>Click to upload</span>
                </div>
              )}
            </div>
            {fieldErrors?.heroImg && <FieldError msg={fieldErrors.heroImg} />}
            <input
              ref={heroInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={async (e) => { const f = e.target.files?.[0]; if (f) { await uploadHero(f); } e.target.value = ''; }}
            />
            {meta.heroImg && (
              <div style={{ position: 'relative', marginTop: 6 }}>
                <input
                  className="be-field__input"
                  value={meta.heroImg}
                  onChange={(e) => onMetaChange({ heroImg: e.target.value })}
                  onBlur={(e) => mirrorHeroToGcs(e.target.value)}
                  placeholder="Or paste image URL"
                  style={{ paddingRight: heroMirroring ? 28 : undefined }}
                />
                {heroMirroring && (
                  <span title="Uploading to GCS…" style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', fontSize: 11, color: '#6c7280' }}>↑ GCS</span>
                )}
              </div>
            )}
            {meta.heroImg && (
              <button
                type="button"
                style={{ marginTop: 6, fontSize: 12, color: 'var(--seasons-color-error-700, #b91c1c)', background: 'none', border: 'none', cursor: 'pointer', padding: '4px 0', fontFamily: 'var(--seasons-font-polysans, system-ui)', display: 'flex', alignItems: 'center', gap: 4 }}
                onClick={() => onMetaChange({ heroImg: '', heroAlt: '' })}
              >
                <svg width="10" height="10" fill="currentColor" viewBox="0 0 256 256"><path d="M205.66,194.34a8,8,0,0,1-11.32,11.32L128,139.31,61.66,205.66a8,8,0,0,1-11.32-11.32L116.69,128,50.34,61.66A8,8,0,0,1,61.66,50.34L128,116.69l66.34-66.35a8,8,0,0,1,11.32,11.32L139.31,128Z"/></svg>
                Remove image
              </button>
            )}
          </div>

          {/* Hero alt */}
          <div className="be-field">
            <label className="be-field__label">Hero Alt Text</label>
            <input
              className="be-field__input"
              value={meta.heroAlt || ''}
              onChange={(e) => onMetaChange({ heroAlt: e.target.value })}
              placeholder="Describe the hero image"
            />
          </div>

          {/* External link — only in External Link mode. Title, Dek, and Hero
              Cover Image above still supply the card's text and thumbnail. */}
          {contentMode === 'external' && (
            <div className="be-field">
              <label className="be-field__label">External Link<RequiredMark /></label>
              <input
                className="be-field__input"
                type="url"
                value={meta.newsLink || ''}
                onChange={(e) => onMetaChange({ newsLink: e.target.value })}
                placeholder="https://example.com/article"
                style={fieldErrors?.newsLink ? { border: '1px solid #fca5a5' } : undefined}
              />
              <FieldError msg={fieldErrors?.newsLink} />
              <span className="be-field__hint">
                This card links straight to this URL instead of an internal page.
              </span>
            </div>
          )}

          {/* PDF — resource posts only, and only in Custom Content mode */}
          {isResource && contentMode === 'custom' && (
            <div className="be-field">
              <label className="be-field__label">PDF<RequiredMark /></label>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input
                  className="be-field__input"
                  style={{ flex: 1, ...(fieldErrors?.pdf && !meta.pdfUrl ? { border: '1px solid #fca5a5' } : {}) }}
                  value={meta.pdfUrl || ''}
                  onChange={(e) => { onMetaChange({ pdfUrl: e.target.value }); if (e.target.value.trim()) onClearError?.('pdf'); }}
                  placeholder="Paste URL or upload a PDF file →"
                />
                <button
                  type="button"
                  className="be-upload-pdf-btn"
                  onClick={() => pdfInputRef.current?.click()}
                  disabled={pdfUploading}
                  title="Upload PDF from your device"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '0 12px',
                    height: 34,
                    border: '1px solid var(--be-border, #d4d4d4)',
                    borderRadius: 6,
                    background: pdfUploading ? 'var(--be-surface-2, #f5f5f5)' : 'var(--be-surface-1, #fff)',
                    color: 'var(--be-text-1, #111)',
                    fontSize: 13,
                    fontWeight: 500,
                    cursor: pdfUploading ? 'default' : 'pointer',
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                  }}
                >
                  {pdfUploading ? (
                    <>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ animation: 'spin 0.8s linear infinite' }}><circle cx="12" cy="12" r="10" strokeOpacity="0.25"/><path d="M12 2a10 10 0 0 1 10 10"/><style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style></svg>
                      Uploading…
                    </>
                  ) : (
                    <>
                      <svg width="14" height="14" viewBox="0 0 256 256" fill="currentColor" aria-hidden="true"><path d="M213.66,82.34l-56-56A8,8,0,0,0,152,24H56A16,16,0,0,0,40,40V216a16,16,0,0,0,16,16H200a16,16,0,0,0,16-16V88A8,8,0,0,0,213.66,82.34ZM160,51.31,188.69,80H160ZM200,216H56V40h88V88a8,8,0,0,0,8,8h48V216Zm-40-80a8,8,0,0,1-8,8H136v16a8,8,0,0,1-16,0V144H104a8,8,0,0,1,0-16h16V112a8,8,0,0,1,16,0v16h16A8,8,0,0,1,160,136Z"/></svg>
                      Upload PDF
                    </>
                  )}
                </button>
                <input
                  ref={pdfInputRef}
                  type="file"
                  accept=".pdf,application/pdf"
                  style={{ display: 'none' }}
                  onChange={async (e) => {
                    const f = e.target.files?.[0];
                    if (f) await uploadPdf(f);
                    e.target.value = '';
                  }}
                />
              </div>
              {meta.pdfUrl && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
                  <a
                    href={meta.pdfUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, color: 'var(--be-accent, #2563eb)', textDecoration: 'none' }}
                  >
                    <svg width="12" height="12" viewBox="0 0 256 256" fill="currentColor" aria-hidden="true"><path d="M224,128a8,8,0,0,1-8,8H136v80a8,8,0,0,1-16,0V136H40a8,8,0,0,1,0-16h80V40a8,8,0,0,1,16,0v80h80A8,8,0,0,1,224,128Z"/></svg>
                    Open PDF
                  </a>
                  <button
                    type="button"
                    style={{ fontSize: 12, color: 'var(--seasons-color-error-700, #b91c1c)', background: 'none', border: 'none', cursor: 'pointer', padding: '2px 6px', fontFamily: 'var(--seasons-font-polysans, system-ui)', display: 'inline-flex', alignItems: 'center', gap: 4 }}
                    onClick={() => onMetaChange({ pdfUrl: '' })}
                  >
                    <svg width="10" height="10" fill="currentColor" viewBox="0 0 256 256"><path d="M205.66,194.34a8,8,0,0,1-11.32,11.32L128,139.31,61.66,205.66a8,8,0,0,1-11.32-11.32L116.69,128,50.34,61.66A8,8,0,0,1,61.66,50.34L128,116.69l66.34-66.35a8,8,0,0,1,11.32,11.32L139.31,128Z"/></svg>
                    Remove PDF
                  </button>
                </div>
              )}
              <FieldError msg={fieldErrors?.pdf} />
              <span className="be-field__hint">Paste an external URL or upload a PDF from your device. Used to render the 3-page preview thumbnail.</span>
            </div>
          )}

          <div className="be-section-divider" />

          {/* Authors */}
          <div className="be-field">
            <label className="be-field__label">
              {!isResource && contentMode === 'custom' ? <><span>Authors</span><RequiredMark /></> : 'Authors'}
            </label>
            {fieldErrors?.author && (
              <FieldError msg={fieldErrors.author} />
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: fieldErrors?.author ? 6 : 0 }}>
              {(meta.authors || []).map((author, idx) => (
                <AuthorRow
                  key={idx}
                  author={author}
                  onChange={(patch) => { updateAuthor(idx, patch); if (patch.name?.trim()) onClearError?.('author'); }}
                  onRemove={() => removeAuthor(idx)}
                  onUploadAvatar={(f) => uploadAvatar(idx, f)}
                />
              ))}
              <button className="be-add-btn" onClick={addAuthor}>
                <svg width="12" height="12" fill="currentColor" viewBox="0 0 256 256" aria-hidden="true"><path d="M224,128a8,8,0,0,1-8,8H136v80a8,8,0,0,1-16,0V136H40a8,8,0,0,1,0-16h80V40a8,8,0,0,1,16,0v80h80A8,8,0,0,1,224,128Z"/></svg>
                Add author
              </button>
            </div>
          </div>

          {!isResource && contentMode === 'custom' && <div className="be-section-divider" />}

          {/* Audio — Custom Content mode only; a link-out card has no article to narrate */}
          {!isResource && contentMode === 'custom' && (
            <>
              <div className="be-field">
                <label className="be-field__label">Audio (for Listen pill)</label>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input
                    className="be-field__input"
                    style={{ flex: 1 }}
                    value={meta.audioUrl || ''}
                    onChange={(e) => onMetaChange({ audioUrl: e.target.value })}
                    placeholder="Paste URL or upload an audio file →"
                  />
                  <button
                    type="button"
                    className="be-upload-pdf-btn"
                    onClick={() => audioInputRef.current?.click()}
                    disabled={audioUploading}
                    title="Upload audio from your device"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      padding: '0 12px',
                      height: 34,
                      border: '1px solid var(--be-border, #d4d4d4)',
                      borderRadius: 6,
                      background: audioUploading ? 'var(--be-surface-2, #f5f5f5)' : 'var(--be-surface-1, #fff)',
                      color: 'var(--be-text-1, #111)',
                      fontSize: 13,
                      fontWeight: 500,
                      cursor: audioUploading ? 'default' : 'pointer',
                      whiteSpace: 'nowrap',
                      flexShrink: 0,
                    }}
                  >
                    {audioUploading ? (
                      <>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ animation: 'spin 0.8s linear infinite' }}><circle cx="12" cy="12" r="10" strokeOpacity="0.25"/><path d="M12 2a10 10 0 0 1 10 10"/><style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style></svg>
                        Uploading…
                      </>
                    ) : (
                      <>
                        <svg width="14" height="14" viewBox="0 0 256 256" fill="currentColor" aria-hidden="true"><path d="M213.66,82.34l-56-56A8,8,0,0,0,152,24H56A16,16,0,0,0,40,40V216a16,16,0,0,0,16,16H200a16,16,0,0,0,16-16V88A8,8,0,0,0,213.66,82.34ZM160,51.31,188.69,80H160ZM200,216H56V40h88V88a8,8,0,0,0,8,8h48V216Zm-28-60a8,8,0,0,1-8,8H136v8a8,8,0,0,1-16,0v-8H112a8,8,0,0,1,0-16h8v-8a8,8,0,0,1,16,0v8h8A8,8,0,0,1,172,156Z"/></svg>
                        Upload Audio
                      </>
                    )}
                  </button>
                  <input
                    ref={audioInputRef}
                    type="file"
                    accept="audio/*"
                    style={{ display: 'none' }}
                    onChange={async (e) => {
                      const f = e.target.files?.[0];
                      if (f) await uploadAudio(f);
                      e.target.value = '';
                    }}
                  />
                </div>
                {meta.audioUrl && (
                  <button
                    type="button"
                    style={{ marginTop: 6, fontSize: 12, color: 'var(--seasons-color-error-700, #b91c1c)', background: 'none', border: 'none', cursor: 'pointer', padding: '4px 0', fontFamily: 'var(--seasons-font-polysans, system-ui)', display: 'flex', alignItems: 'center', gap: 4 }}
                    onClick={() => onMetaChange({ audioUrl: '', audioDuration: '' })}
                  >
                    <svg width="10" height="10" fill="currentColor" viewBox="0 0 256 256"><path d="M205.66,194.34a8,8,0,0,1-11.32,11.32L128,139.31,61.66,205.66a8,8,0,0,1-11.32-11.32L116.69,128,50.34,61.66A8,8,0,0,1,61.66,50.34L128,116.69l66.34-66.35a8,8,0,0,1,11.32,11.32L139.31,128Z"/></svg>
                    Remove audio
                  </button>
                )}
              </div>

              <div className="be-field">
                <label className="be-field__label">Audio Duration Label</label>
                <input
                  className="be-field__input"
                  value={meta.audioDuration || ''}
                  onChange={(e) => onMetaChange({ audioDuration: e.target.value })}
                  placeholder="08:42"
                />
              </div>
            </>
          )}


          {contentMode === 'custom' && (
            <>
              <div className="be-section-divider" />

              {/* ── SEO / Meta ─────────────────────────────────────────── */}
              <p className="be-field__section-heading">SEO &amp; Meta</p>

              <div className="be-field">
                <label className="be-field__label">Meta Title</label>
                <input
                  className="be-field__input"
                  value={meta.metaTitle || ''}
                  onChange={(e) => onMetaChange({ metaTitle: e.target.value })}
                  placeholder="Page title for search engines (defaults to post title)"
                />
              </div>

              <div className="be-field">
                <label className="be-field__label">Meta Description</label>
                <textarea
                  className="be-field__textarea"
                  value={meta.metaDescription || ''}
                  onChange={(e) => onMetaChange({ metaDescription: e.target.value })}
                  placeholder="150–160 character summary for search results"
                  rows={3}
                />
              </div>

              <div className="be-field">
                <label className="be-field__label">
                  Meta Image URL<RequiredMark />
                </label>
                <p style={{ fontSize: 11, color: 'var(--be-text-secondary, #666)', margin: '0 0 6px', lineHeight: 1.4 }}>
                  Required — the image used for Open Graph / Twitter card link previews (e.g. when shared on LinkedIn or Slack).
                </p>
                <div style={{ display: 'flex', gap: 6 }}>
                  <input
                    className="be-field__input"
                    value={meta.metaImage || ''}
                    onChange={(e) => onMetaChange({ metaImage: e.target.value })}
                    placeholder="Open Graph / Twitter card image URL"
                    style={{ flex: 1, ...(fieldErrors?.metaImage ? { border: '1px solid #fca5a5' } : {}) }}
                  />
                  <button
                    type="button"
                    className="btn-simple btn-simple--sm btn-simple--outline"
                    onClick={() => metaImageInputRef.current?.click()}
                    title="Upload image to GCS"
                  >Upload</button>
                </div>
                {fieldErrors?.metaImage && <FieldError msg={fieldErrors.metaImage} />}
                <input
                  ref={metaImageInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={async (e) => {
                    const f = e.target.files?.[0];
                    e.target.value = '';
                    if (f) { const url = await uploadFile(f); onMetaChange({ metaImage: url }); }
                  }}
                />
              </div>

              <div className="be-field">
                <label className="be-field__label">Meta Keywords</label>
                <input
                  className="be-field__input"
                  value={meta.metaKeywords || ''}
                  onChange={(e) => onMetaChange({ metaKeywords: e.target.value })}
                  placeholder="comma, separated, keywords"
                />
              </div>

              <div className="be-field">
                <label className="be-field__label">Meta Robot</label>
                <input
                  className="be-field__input"
                  value={meta.metaRobot || ''}
                  onChange={(e) => onMetaChange({ metaRobot: e.target.value })}
                  placeholder="index, follow"
                />
              </div>
            </>
          )}
        </div>

        {/* Sticky footer — Save + Publish */}
        {(onSave || publishToggle) && (
          <div className="be-settings-panel__footer">
            {onSave && (
              <button
                className="block-button block-button--sm block-button--primary"
                onClick={onSave}
                disabled={saving}
              >
                <div className="block-button__text">
                  <span className="btn-primary-text">{saving ? 'Saving…' : 'Save'}</span>
                  <span className="btn-hover-text">{saving ? 'Saving…' : 'Save'}</span>
                </div>
              </button>
            )}
            {publishToggle && (
              <div style={{ flex: 1, display: 'flex', ...(hasErrors ? { pointerEvents: 'none', opacity: 0.45, cursor: 'not-allowed' } : {}) }}>
                {publishToggle}
              </div>
            )}
          </div>
        )}
      </aside>
    </>
  );
}

function AuthorRow({ author, onChange, onRemove, onUploadAvatar }: {
  author: PostAuthor;
  onChange: (p: Partial<PostAuthor>) => void;
  onRemove: () => void;
  onUploadAvatar: (f: File) => void;
}) {
  const avatarInputRef = useRef<HTMLInputElement>(null);
  return (
    <div className="be-author-row">
      <div className="be-author-avatar" onClick={() => avatarInputRef.current?.click()} title="Click to upload avatar">
        {author.avatar ? <img src={author.avatar} alt={author.name} /> : (
          <svg width="18" height="18" fill="currentColor" viewBox="0 0 256 256" aria-hidden="true"><path d="M230.92,212c-15.23-26.33-38.7-45.21-66.09-54.16a72,72,0,1,0-73.66,0C63.78,166.78,40.31,185.66,25.08,212a8,8,0,1,0,13.85,8c18.84-32.56,52.14-52,89.07-52s70.23,19.44,89.07,52a8,8,0,1,0,13.85-8ZM72,96a56,56,0,1,1,56,56A56.06,56.06,0,0,1,72,96Z"/></svg>
        )}
        <input ref={avatarInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={(e) => { const f = e.target.files?.[0]; if (f) onUploadAvatar(f); e.target.value = ''; }} />
      </div>
      <div className="be-author-fields">
        <input
          className="be-field__input"
          value={author.name || ''}
          onChange={(e) => onChange({ name: e.target.value })}
          placeholder="Name"
          style={{ fontSize: 13, padding: '5px 8px' }}
        />
        <input
          className="be-field__input"
          value={author.profileUrl || ''}
          onChange={(e) => onChange({ profileUrl: e.target.value })}
          placeholder="Profile URL"
          style={{ fontSize: 13, padding: '5px 8px' }}
        />
      </div>
      <button className="be-author-remove" onClick={onRemove} aria-label="Remove author">
        <svg width="13" height="13" fill="currentColor" viewBox="0 0 256 256" aria-hidden="true"><path d="M205.66,194.34a8,8,0,0,1-11.32,11.32L128,139.31,61.66,205.66a8,8,0,0,1-11.32-11.32L116.69,128,50.34,61.66A8,8,0,0,1,61.66,50.34L128,116.69l66.34-66.35a8,8,0,0,1,11.32,11.32L139.31,128Z"/></svg>
      </button>
    </div>
  );
}
